import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../../../firebase/config';
import { collection, query, where, getDocs, doc, deleteDoc, getDoc } from 'firebase/firestore';
import ModalConfirmacion from '../../../ui/ModalConfirmacion'; 

// --- HELPERS DE FORMATO ---
const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '$0,00';
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);
};

const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return `${parseFloat(value)}%`;
};

// --- ICONOS DE ALTA FIDELIDAD ---
const IconTrendingUp = () => <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>;
const IconCalculator = () => <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 11h.01M12 14h.01M15 11h.01M15 14h.01M13 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V9l-6-6z" /></svg>;
const IconTrash = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const IconReceipt = () => <svg className="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;

const DistribucionTabContrato = ({ contrato }) => {
    const [liquidaciones, setLiquidaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [formaLiquidacionReal, setFormaLiquidacionReal] = useState('-');
    
    // Reglas Frescas (Solo para el Fallback de registros legacy que no tengan la foto histórica)
    const [freshRules, setFreshRules] = useState(null);

    // --- 1. CARGA DE REGLAS POR JERARQUÍA (FALLBACK SEGURO PARA REGISTROS ANTERIORES) ---
    useEffect(() => {
        const fetchRules = async () => {
            if (!contrato) return;
            try {
                let rules = { canalPct: 0, canalDesc: 0, subPct: 0, subDesc: 0 };
                
                // FASE A: PORCENTAJES DE LA PÓLIZA
                if (contrato.comisionOrganizador || contrato.comisionAsociado) {
                    rules.canalPct = parseFloat(contrato.comisionOrganizador) || 0;
                    rules.subPct = parseFloat(contrato.comisionAsociado) || 0;
                }

                // FASE B: MAESTRO DE SUBCANAL
                if (contrato.subcanalNombreId) {
                    const subRef = doc(db, 'public/data/subcanales', contrato.subcanalNombreId);
                    const snapSub = await getDoc(subRef);
                    if (snapSub.exists()) {
                        const sData = snapSub.data();
                        const r = sData.artReglas?.find(x => 
                            x.compania?.trim().toUpperCase() === contrato.compania?.trim().toUpperCase()
                        );
                        if (r) {
                            rules.subDesc = parseFloat(r.descSub) || 0;
                            if (parseFloat(r.descCanal) > 0) rules.canalDesc = parseFloat(r.descCanal);
                            if (rules.subPct === 0) rules.subPct = parseFloat(r.distSub) || 0;
                            if (rules.canalPct === 0) rules.canalPct = parseFloat(r.distCanal) || 0;
                        }
                    }
                }

                // FASE C: MAESTRO DE CANAL
                if (contrato.canalNombreId) {
                    const canalRef = doc(db, 'public/data/canales', contrato.canalNombreId);
                    const snapC = await getDoc(canalRef);
                    if (snapC.exists()) {
                        const cData = snapC.data();
                        setFormaLiquidacionReal(cData.formaLiquidarART || 'Comisión');
                        if (rules.canalDesc === 0) rules.canalDesc = parseFloat(cData.descuento || 0);
                        if (rules.canalPct === 0) {
                            const rEspecial = cData.artEspeciales?.find(x => 
                                x.compania?.trim().toUpperCase() === contrato.compania?.trim().toUpperCase()
                            );
                            rules.canalPct = rEspecial ? parseFloat(rEspecial.porcentaje) : parseFloat(cData.porcentajePatrimoniales || 0);
                        }
                    }
                }
                setFreshRules(rules);
            } catch (e) { 
                console.error("Error cargando jerarquía de fallback ART:", e); 
            }
        };
        fetchRules();
    }, [contrato]);

    // --- 2. CARGA DE LIQUIDACIONES EN TIEMPO REAL ---
    const fetchLiquidaciones = async () => {
        if (!contrato.nContrato) { setLoading(false); return; }
        setLoading(true);
        try {
            const q = query(collection(db, 'public/data/liquidaciones'), where('nContrato', '==', contrato.nContrato)); 
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => (b.fechaImportacion || '').localeCompare(a.fechaImportacion || ''));
            setLiquidaciones(data);
        } catch (error) { 
            console.error("Error cargando liquidaciones:", error); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        fetchLiquidaciones();
    }, [contrato.nContrato]);

    // --- 3. ANULACIÓN DE REGISTRO ---
    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteDoc(doc(db, 'public/data/liquidaciones', itemToDelete.id));
            setLiquidaciones(prev => prev.filter(l => l.id !== itemToDelete.id));
            setItemToDelete(null);
        } catch (error) { 
            alert("Error al eliminar la liquidación."); 
        }
    };

    // --- 4. CÁLCULO DE FILA - RESGUARDO HISTÓRICO SÍNCRONO ---
    const calculateRow = (liq) => {
        const isPrima = liq.formaLiquidacion === 'Prima';
        const base = isPrima ? (liq.importePrima || 0) : (liq.importeComision || 0);
        
        // Evaluamos si el registro tiene la foto de comisiones guardada (Snapshot)
        const hasSnapshot = liq.comisionOrganizador !== undefined || liq.comisionAsociado !== undefined;

        // Porcentajes
        const canalPct = hasSnapshot ? (liq.comisionOrganizador || 0) : (freshRules?.canalPct || parseFloat(contrato.comisionOrganizador) || 0);
        const subPct = hasSnapshot ? (liq.comisionAsociado || 0) : (freshRules?.subPct || parseFloat(contrato.comisionAsociado) || 0);
        
        // Descuentos
        const canalDesc = hasSnapshot ? (liq.descuentoOrganizador || 0) : (freshRules?.canalDesc || parseFloat(contrato.descuentoOrganizador) || 0);
        const subDesc = hasSnapshot ? (liq.descuentoAsociado || 0) : (freshRules?.subDesc || parseFloat(contrato.descuentoAsociado) || 0);

        // Cálculos Canal
        const cBruto = base * (canalPct / 100);
        const cNeto = cBruto - (cBruto * (canalDesc / 100));

        // Cálculos Subcanal
        const sBruto = base * (subPct / 100);
        const sNeto = sBruto - (sBruto * (subDesc / 100));

        return { 
            base, 
            cBruto, 
            cNeto, 
            sBruto, 
            sNeto, 
            rules: { canalPct, canalDesc, subPct, subDesc } 
        };
    };

    // --- 5. ACUMULADOS INTEGRADOS PARA EL DASHBOARD FINANCIERO (useMemo) ---
    const totales = useMemo(() => {
        let prima = 0;
        let comision = 0;
        let canalNeto = 0;
        let subcanalNeto = 0;

        liquidaciones.forEach(liq => {
            const calc = calculateRow(liq);
            prima += parseFloat(liq.importePrima || 0);
            comision += parseFloat(liq.importeComision || 0);
            canalNeto += calc.cNeto;
            subcanalNeto += calc.sNeto;
        });

        return { prima, comision, canalNeto, subcanalNeto };
    }, [liquidaciones, freshRules, contrato]);

    return (
        <div className="space-y-6 animate-fade-in text-left">
            
            {/* PANEL DE MÉTRICAS FINANCIERAS (DASHBOARD) */}
            {!loading && liquidaciones.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="text-left">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Total Primas</span>
                            <span className="text-lg font-extrabold text-slate-800 leading-tight mt-1 block">{formatCurrency(totales.prima)}</span>
                        </div>
                        <div className="bg-slate-100 p-2 rounded-lg text-slate-600"><IconReceipt /></div>
                    </div>
                    
                    <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="text-left">
                            <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest block">Total Comisiones</span>
                            <span className="text-lg font-extrabold text-amber-800 leading-tight mt-1 block">{formatCurrency(totales.comision)}</span>
                        </div>
                        <div className="bg-amber-100/50 p-2 rounded-lg text-amber-600"><IconCalculator /></div>
                    </div>

                    <div className="bg-indigo-50/50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="text-left">
                            <span className="text-[10px] font-black text-indigo-700 uppercase tracking-widest block">Neto Canal</span>
                            <span className="text-lg font-extrabold text-indigo-800 leading-tight mt-1 block">{formatCurrency(totales.canalNeto)}</span>
                        </div>
                        <div className="bg-indigo-100/50 p-2 rounded-lg text-indigo-600"><IconTrendingUp /></div>
                    </div>

                    <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="text-left">
                            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest block">Neto Subcanal</span>
                            <span className="text-lg font-extrabold text-emerald-800 leading-tight mt-1 block">{formatCurrency(totales.subcanalNeto)}</span>
                        </div>
                        <div className="bg-emerald-100/50 p-2 rounded-lg text-emerald-600"><IconTrendingUp /></div>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center select-none border-b border-slate-100 pb-3">
                <div>
                    <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight text-left">Distribución de Liquidaciones ART</h3>
                    <p className="text-xs text-slate-400 mt-0.5">Control histórico de acreditación de comisiones de seguros.</p>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center p-16 text-center">
                    <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs text-slate-400 font-bold uppercase mt-3 tracking-widest animate-pulse">Sincronizando cuentas...</span>
                </div>
            ) : liquidaciones.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
                    <IconReceipt />
                    <p className="text-slate-500 font-bold text-base">No hay liquidaciones importadas para este contrato.</p>
                    <p className="text-slate-400 text-xs mt-1">Sube la remesa mensual de comisiones en el importador masivo para visualizar el desglose.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs text-slate-600 whitespace-nowrap border-collapse">
                            <thead>
                                <tr className="bg-slate-900 text-white uppercase font-black text-[9px] tracking-wider select-none">
                                    <th colSpan="4" className="px-4 py-3 text-center border-r border-slate-800 bg-slate-800">Datos Importados</th>
                                    <th colSpan="6" className="px-4 py-3 text-center border-r border-slate-800 bg-indigo-950">
                                        DISTRIBUCIÓN CANAL ({contrato.canalNombre || 'S/D'})
                                    </th>
                                    <th colSpan="6" className="px-4 py-3 text-center border-r border-slate-800 bg-emerald-950">
                                        DISTRIBUCIÓN SUBCANAL ({contrato.subcanalNombre || '-'})
                                    </th>
                                    <th className="px-2 py-3 bg-slate-900"></th>
                                </tr>
                                <tr className="bg-slate-50 text-slate-500 border-b border-slate-200 font-black text-[9px] tracking-wider select-none">
                                    <th className="px-4 py-2 border-r border-slate-200">Periodo</th>
                                    <th className="px-4 py-2 text-center border-r border-slate-200">Forma</th>
                                    <th className="px-4 py-2 text-right border-r border-slate-200">Prima $</th>
                                    <th className="px-4 py-2 bg-yellow-50/50 text-slate-900 text-right font-black border-r border-slate-300">Comisión $</th>
                                    
                                    <th className="px-4 py-2 border-r border-slate-100">Tipo</th>
                                    <th className="px-4 py-2 border-r border-slate-100">Nombre</th>
                                    <th className="px-2 py-2 text-center">% Dist</th>
                                    <th className="px-2 py-2 text-center border-r border-slate-100">% Desc</th>
                                    <th className="px-3 py-2 text-right border-r border-white font-semibold">Bruta $</th>
                                    <th className="px-3 py-2 bg-indigo-50 text-indigo-900 text-right font-black border-r border-slate-100">Neta $</th>

                                    <th className="px-4 py-2 border-r border-slate-100">Nombre</th>
                                    <th className="px-2 py-2 text-center">% Dist</th>
                                    <th className="px-2 py-2 text-center border-r border-slate-100">% Desc</th>
                                    <th className="px-3 py-2 text-right font-semibold border-r border-white">Bruta $</th>
                                    <th className="px-3 py-2 bg-emerald-50 text-emerald-900 text-right font-black">Neta $</th>
                                    
                                    <th className="px-2 py-2 text-center"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-left">
                                {liquidaciones.map((l) => {
                                    const calc = calculateRow(l);
                                    return (
                                        <tr key={l.id} className="hover:bg-slate-50/50 transition-colors">
                                            {/* Datos Base */}
                                            <td className="px-4 py-3.5 font-bold text-slate-900 border-r border-slate-100 tabular-nums">{l.periodo}</td>
                                            <td className="px-4 py-3.5 text-center border-r border-slate-100">
                                                <span className="bg-slate-100 px-2 py-0.5 rounded text-[8px] font-black text-slate-500 border border-slate-200 uppercase tracking-wider">
                                                    {l.formaLiquidacion || formaLiquidacionReal}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5 text-right text-slate-500 border-r border-slate-100 font-mono font-bold">{formatCurrency(l.importePrima || 0)}</td>
                                            <td className="px-4 py-3.5 text-right font-black text-slate-900 bg-yellow-50/20 border-r border-slate-200 font-mono">{formatCurrency(l.importeComision || 0)}</td>

                                            {/* Desglose Canal */}
                                            <td className="px-4 py-3.5 border-r border-slate-50 font-bold uppercase tracking-tight text-[10px] text-slate-400">{l.canal || contrato.canal}</td>
                                            <td className="px-4 py-3.5 border-r border-slate-50 truncate max-w-[120px] font-medium" title={l.canalNombre || contrato.canalNombre}>{l.canalNombre || contrato.canalNombre || '-'}</td>
                                            <td className="px-2 py-3.5 text-center text-indigo-600 font-black font-mono">{formatPercent(calc.rules.canalPct)}</td>
                                            <td className="px-2 py-3.5 text-center text-rose-500 border-r border-slate-50 font-black font-mono">
                                                {calc.rules.canalDesc > 0 ? `-${calc.rules.canalDesc}%` : '-'}
                                            </td>
                                            <td className="px-3 py-3.5 text-right font-medium text-slate-500 border-r border-white font-mono">{formatCurrency(calc.cBruto)}</td>
                                            <td className="px-3 py-3.5 text-right font-black text-indigo-700 bg-indigo-50/50 border-r border-slate-100 font-mono">{formatCurrency(calc.cNeto)}</td>

                                            {/* Desglose Subcanal */}
                                            <td className="px-4 py-3.5 border-r border-slate-50 truncate max-w-[120px] font-medium" title={l.subcanalNombre || contrato.subcanalNombre}>{l.subcanalNombre || contrato.subcanalNombre || '-'}</td>
                                            <td className="px-2 py-3.5 text-center text-emerald-600 font-black font-mono">{formatPercent(calc.rules.subPct)}</td>
                                            <td className="px-2 py-3.5 text-center text-rose-500 border-r border-slate-50 font-black font-mono">
                                                {calc.rules.subDesc > 0 ? `-${calc.rules.subDesc}%` : '-'}
                                            </td>
                                            <td className="px-3 py-3.5 text-right font-medium text-slate-500 border-r border-white font-mono">{formatCurrency(calc.sBruto)}</td>
                                            <td className="px-3 py-3.5 text-right font-black text-emerald-700 bg-emerald-50/50 font-mono">{formatCurrency(calc.sNeto)}</td>

                                            {/* Acción Borrar */}
                                            <td className="px-3 py-3.5 text-center sticky right-0 z-20 bg-white group-hover:bg-slate-50 border-l border-slate-100">
                                                <button 
                                                    type="button"
                                                    onClick={() => setItemToDelete(l)} 
                                                    className="text-slate-300 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-all active:scale-90"
                                                    title="Eliminar Liquidación"
                                                >
                                                    <IconTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            <ModalConfirmacion 
                show={!!itemToDelete} 
                onClose={() => setItemToDelete(null)} 
                onConfirm={handleDelete} 
                title="Eliminar Liquidación de ART" 
                message={`¿Estás seguro de eliminar este registro de liquidación del periodo ${itemToDelete?.periodo}? Esta acción restará de forma masiva los saldos y no se puede deshacer.`} 
            />
        </div>
    );
};

export default DistribucionTabContrato;